"use client";

import { SteamLogo } from "@/components/icons";
import { Button } from "@/components/ui/button";
import {
  Action,
  ControlType,
} from "@/context/AnalyticsProvider/AnalyticsProvider";
import { GamePageSection } from "@/context/AnalyticsProvider/gamePage";
import { useGameDetailsContext } from "@/context/GameDetailsContext";
import { useGameOnboardingContext } from "@/context/GameOnboardingContext";
import { ExternalLinkIcon } from "lucide-react";
import Image from "next/image";
import { useCallback, useState } from "react";
import { GlassButton } from "../GlassButton";
import type { GPOnboardingStepProps } from "./utils";

export const WishlistSteamVerify = ({
  onNext,
  isVisible,
  track,
}: GPOnboardingStepProps) => {
  const { validateSocialQuest } = useGameOnboardingContext();
  const { gameData } = useGameDetailsContext();
  const [isVerifying, setIsVerifying] = useState(false);
  const [verificationFailed, setVerificationFailed] = useState(false);

  const handleVerify = useCallback(async () => {
    if (!isVerifying) return onNext();
    if (isVerifying) return;
    setIsVerifying(true);
    setVerificationFailed(false);

    track({
      action: Action.PRESSED,
      extras: {
        control: "verify-wishlist-retry",
        controlType: ControlType.BUTTON,
        section: GamePageSection.ONBOARDING_MODAL,
      },
    });

    try {
      const following = await validateSocialQuest(gameData);
      if (following) {
        track({
          action: Action.SUCCEEDED,
          extras: {
            control: "steam-wishlist",
            section: GamePageSection.ONBOARDING_MODAL,
          },
        });
        onNext();
      } else {
        setVerificationFailed(true);
        // Stay on this step if validation fails again
        track({
          action: Action.FAILED,
          extras: {
            control: "steam-wishlist",
            section: GamePageSection.ONBOARDING_MODAL,
            controlLabel: "Verification failed again",
          },
        });
      }
    } catch (error) {
      console.error("Validation error:", error);
      setVerificationFailed(true);
    } finally {
      setIsVerifying(false);
    }
  }, [isVerifying, validateSocialQuest, gameData, track, onNext]);

  const handleChangeSettings = useCallback(() => {
    window.open("https://steamcommunity.com/my/edit/settings", "_blank");
    track({
      action: Action.PRESSED,
      extras: {
        control: "change-steam-settings",
        controlType: ControlType.BUTTON,
        section: GamePageSection.ONBOARDING_MODAL,
      },
    });
  }, [track]);

  const handleAddToSteamWishlist = useCallback(() => {
    window.open("https://store.steampowered.com/app/3760100", "_blank");
    track({
      action: Action.PRESSED,
      extras: {
        control: "add-to-wishlist-link",
        controlType: ControlType.LINK,
        section: GamePageSection.ONBOARDING_MODAL,
      },
    });
  }, [track]);

  return (
    <div className="flex flex-col items-center justify-start w-full min-h-[100dvh] md:h-full px-4 pt-0 pb-6 md:px-8">
      <div className="flex flex-col items-center justify-center w-full gap-1 md:gap-2 mb-2 md:mb-8">
        <h2 className="font-bold text-2xl md:text-3xl text-[#f7f7f7] leading-tight w-full text-center md:whitespace-nowrap">
          Unable to Verify Wishlist
        </h2>
      </div>

      <div className="flex flex-col items-center w-full gap-5 md:gap-8">
        <div className="flex flex-col items-center w-full gap-4 md:gap-4">
          <div className="flex flex-col items-center w-full gap-3 md:gap-3">
            <p className="text-xs md:text-sm text-white opacity-70 text-center leading-5">
              Your profile and game details must be public.
              <br />
              This screenshot shows the Steam settings to change:
            </p>
            <div className="w-auto max-w-[240px] md:max-w-[300px] flex items-center justify-center">
              <Image
                src="/assets/steam-onboarding/steam-settings.webp"
                alt="Steam Settings Screenshot"
                width={300}
                height={243}
                className="w-full h-auto object-contain rounded-[2px]"
              />
            </div>
          </div>
        </div>

        <div className="w-full flex flex-col gap-2 items-center mt-4 md:mt-2">
          <div className="w-full flex flex-col gap-3 md:gap-4 items-center">
            <Button
              onClick={handleChangeSettings}
              className="w-full h-10 md:h-12 px-4 md:px-6 rounded-[26px] border-[2px] border-[#f7f7f7] bg-[rgba(255,255,255,0.05)] hover:bg-white/10 text-[#fafafa] font-medium text-sm md:text-lg gap-2 shadow-[0px_1px_3px_0px_rgba(0,0,0,0.16),0px_1px_3px_0px_rgba(0,0,0,0.32)]"
              variant="ghost"
            >
              Change your settings
              <ExternalLinkIcon className="w-4 h-4 md:w-5 md:h-5" />
            </Button>

            <GlassButton
              control="verify-steam-wishlist"
              section={GamePageSection.ONBOARDING_MODAL}
              onClick={handleVerify}
              disabled={isVerifying}
              variant="cta"
              className="!h-auto w-full py-3 mt-2 min-h-[3rem]"
            >
              <SteamLogo className="mr-2" />
              {isVerifying ? "Verifying..." : "Verify Wishlist"}
            </GlassButton>
          </div>
          {/* Fixed height container to prevent layout shift when error appears */}
          <div className="min-h-[1.5rem] md:min-h-[2.5rem] flex items-start justify-center mt-1 md:mt-3 mb-1 md:mb-0">
            {verificationFailed && (
              <p className="text-xs md:text-sm text-red-500 font-medium animate-in fade-in slide-in-from-top-1 text-center">
                Unable to verify. Make sure your profile and game details are
                public.
              </p>
            )}
          </div>
        </div>

        <div className="flex flex-col items-center justify-center w-full -mt-4">
          <p className="text-xs md:text-sm text-white opacity-70 text-center leading-5">
            Still need to Wishlist 8 Ball on Steam?{" "}
          </p>
          <button
            className="text-sm md:text-base text-white underline flex items-center gap-1 hover:opacity-80"
            type="button"
            onClick={handleAddToSteamWishlist}
          >
            <SteamLogo className="mr-2" />
            Add to your wishlist
          </button>
        </div>
      </div>
    </div>
  );
};
